import strings from './strings.json';

type Lang = string;
type I18nLeaf = Record<string, string>;
type I18nNode = { [key: string]: I18nNode | I18nLeaf };

let currentLang: Lang = 'en';

export function setLanguage(lang: Lang): void {
  currentLang = lang;
}

export function getLanguage(): Lang {
  return currentLang;
}

export function getAvailableLanguages(): Array<{ code: string; label: string }> {
  const i18nNode = (strings as I18nNode).i18n;
  const languagesNode = i18nNode && (i18nNode as I18nNode).languages;
  if (!languagesNode || typeof languagesNode !== 'object') {
    return [{ code: 'en', label: 'English' }];
  }

  const entries = Object.entries(languagesNode as I18nNode)
    .map(([code, data]) => {
      const label = (data as I18nNode)?.label as I18nLeaf | undefined;
      const value = label?.[currentLang] ?? label?.en ?? code;
      return { code, label: value };
    });

  return entries.length ? entries : [{ code: 'en', label: 'English' }];
}

export function t(path: string, vars?: Record<string, string | number>): string {
  const node = resolvePath(path);
  if (!node || typeof node !== 'object') {
    return `[i18n missing: ${path}]`;
  }

  const leaf = node as I18nLeaf;
  const raw = leaf[currentLang] ?? leaf.en;
  if (!raw) {
    return `[i18n missing: ${path}]`;
  }

  if (!vars) return raw;

  return raw.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = vars[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

export function tOrFallback(path: string, fallback: string, vars?: Record<string, string | number>): string {
  const node = resolvePath(path);
  if (!node || typeof node !== 'object') {
    return fallback;
  }

  const leaf = node as I18nLeaf;
  const raw = leaf[currentLang] ?? leaf.en;
  if (!raw) {
    return fallback;
  }

  if (!vars) return raw;

  return raw.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = vars[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

function resolvePath(path: string): I18nNode | I18nLeaf | undefined {
  const parts = path.split('.').filter(Boolean);
  let node: I18nNode | I18nLeaf | undefined = strings as I18nNode;
  for (const part of parts) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as I18nNode)[part];
  }
  return node;
}
